import React, { useEffect, useRef } from "react";
import OSIcon32 from "../../icons/OSIcon32";

/**
 * Compact left-rail queue of pitches for Live Review mode.
 * Each row shows business name, founder, chapter/quarter, rating chips
 * (review-mark icon + count, same fills as the Review Pitches list), and a
 * shortlist mark. Clicking a row sets focus.
 *
 * Keyboard navigation lives in the parent (LiveReviewPane) — this component
 * only renders and reports clicks.
 */

const RATING_CHIP_CLASS = {
  Favorite: "win95-live-review-queue-row__chip--favorite",
  Consideration: "win95-live-review-queue-row__chip--consideration",
  Pass: "win95-live-review-queue-row__chip--pass",
  Ineligible: "win95-live-review-queue-row__chip--ineligible",
  "No Rating": "win95-live-review-queue-row__chip--norating",
};

const RATING_ICON = {
  Favorite: "favorite",
  Consideration: "consider",
  Pass: "pass",
  Ineligible: "ineligible",
  "No Rating": "norating",
};

const RATING_ORDER = { Favorite: 1, Consideration: 2, Pass: 3, Ineligible: 4, "No Rating": 5 };

const LEGEND_ITEMS = [
  { rating: "Favorite", label: "Fav", className: "win95-live-review-queue-row__chip--favorite" },
  { rating: "Consideration", label: "Con", className: "win95-live-review-queue-row__chip--consideration" },
  { rating: "Pass", label: "Pass", className: "win95-live-review-queue-row__chip--pass" },
  { rating: "Ineligible", label: "Inel", className: "win95-live-review-queue-row__chip--ineligible" },
];

function LiveReviewQueue({
  pitches,
  focusedPitchId,
  onFocus,
  getGroupedReviewsForAdmin,
}) {
  const focusedIndex = pitches.findIndex((p) => p.id === focusedPitchId);
  const statusLabel = pitches.length === 0
    ? "0 pitches"
    : `${focusedIndex >= 0 ? focusedIndex + 1 : "—"} of ${pitches.length}`;

  // Keep the active row visible when focus moves. scrollIntoView({block:"nearest"})
  // walks up every scrollable ancestor — including document — so on initial
  // focus snap it could scroll the whole page, hiding the portal titlebar.
  // Scroll only the queue's own list container instead.
  const activeRowRef = useRef(null);
  const listRef = useRef(null);
  useEffect(() => {
    const row = activeRowRef.current;
    const container = listRef.current;
    if (!row || !container) return;
    const rowTop = row.offsetTop - container.offsetTop;
    const rowBottom = rowTop + row.offsetHeight;
    if (rowTop < container.scrollTop) {
      container.scrollTop = rowTop;
    } else if (rowBottom > container.scrollTop + container.clientHeight) {
      container.scrollTop = rowBottom - container.clientHeight;
    }
  }, [focusedPitchId]);

  return (
    <div className="win95-live-review-queue" role="region" aria-label="Pitch queue">
      <div className="win95-live-review-queue__header">
        <div className="win95-live-review-queue__status">{statusLabel}</div>
        <div className="win95-live-review-queue__legend" aria-label="Rating chip legend">
          {LEGEND_ITEMS.map((item) => (
            <span
              key={item.rating}
              className={`win95-live-review-queue__legend-item ${item.className}`}
              title={item.rating}
            >
              <OSIcon32 kind={RATING_ICON[item.rating]} size={11} />
              {item.label}
            </span>
          ))}
        </div>
      </div>
      <div ref={listRef} className="win95-live-review-queue__list" role="listbox" aria-label="Pitches">
        {pitches.length === 0 ? (
          <div className="win95-live-review-queue__empty">
            No pitches match your filters.
          </div>
        ) : (
          pitches.map((p) => {
            const isActive = p.id === focusedPitchId;
            const grouped = getGroupedReviewsForAdmin(p.id);
            const chips = Object.entries(grouped.byRating || {}).sort(
              ([a], [b]) => (RATING_ORDER[a] || 99) - (RATING_ORDER[b] || 99)
            );
            return (
              <button
                key={p.id}
                ref={isActive ? activeRowRef : undefined}
                type="button"
                role="option"
                aria-selected={isActive}
                className={
                  "win95-live-review-queue-row" +
                  (isActive ? " win95-live-review-queue-row--active" : "")
                }
                onClick={() => onFocus(p.id)}
              >
                <div className="win95-live-review-queue-row__title-line">
                  <span className="win95-live-review-queue-row__title">
                    {p.businessName || "N/A"}
                  </span>
                  {p.shortlisted && (
                    <span
                      className="win95-live-review-queue-row__shortlist-mark"
                      title="Shortlisted"
                      aria-label="Shortlisted"
                    >
                      <OSIcon32 kind="shortlist" size={14} />
                    </span>
                  )}
                </div>
                <div className="win95-live-review-queue-row__by">
                  by {p.founderName || "N/A"}
                </div>
                <div className="win95-live-review-queue-row__meta">
                  {p.chapter || "—"} · {p.quarter || "—"}
                </div>
                {chips.length > 0 && (
                  <div className="win95-live-review-queue-row__chips">
                    {chips.map(([rating, data]) => (
                      <span
                        key={rating}
                        className={
                          "win95-live-review-queue-row__chip " +
                          (RATING_CHIP_CLASS[rating] || "")
                        }
                        title={`${rating}: ${data.count}`}
                      >
                        <OSIcon32 kind={RATING_ICON[rating] || "norating"} size={11} />
                        {data.count}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

export default LiveReviewQueue;
